import type { ComponentProps } from 'react';
import React, { memo, useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';

import { type PinData, type Region, TextLink } from '@sg/garnish';
import {
  BodyText,
  Button,
  CloudinaryImage,
  findCenterRegion,
  getIosModalPresentationConfig,
  TagAlert,
  theme,
  useResponsive,
} from '@sg/garnish';
import { Map } from '@sg/radar-fe';

import { getEnvVars } from '@order/utils';

import { Modal } from '../../../../../../../Modal';
import { LOCATION_CARD_IMG_BG_COLOR } from '../../../../LocationResults.constants';
import { LocationResultCardCTAs } from '../LocationResultCardCTAs';

import type { Location, OnSelectedLocationCardPress } from '../../LocationResultCard.types';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultCardDetailsModal = memo((props: LocationResultCardModalProps) => {
  const {
    location,
    locationOrderChannel,
    addressDetails,
    visible,
    isLoading = false,
    isDisabled = false,
    onRequestClose,
    onOrderNowBtnPress,
  } = props;

  const {
    name,
    imageUrl,
    acceptingOrders: isAcceptingOrders = true,
    isClosed,
    isOutpost,
    flexMessage,
    notAcceptingOrdersReason,
    sanitaryGradeDisplayText,
    sanitaryGradeUrl,
  } = location;

  const { formatMessage } = useIntl();
  const { match, minWidth, currentBreakpoint } = useResponsive();

  const ctaLabelForPickupOutpostLocation = isAcceptingOrders
    ? formatMessage(messages.locationOrderNow)
    : formatMessage(messages.locationClosed);
  const ctaLabelForDeliveryLocation = isClosed
    ? formatMessage(messages.locationResultsCardOrderLater)
    : formatMessage(messages.locationResultsCardOrderNow);
  const ctaLabel =
    locationOrderChannel === 'delivery'
      ? ctaLabelForDeliveryLocation
      : ctaLabelForPickupOutpostLocation;

  // NOTE: Because many outpost locations lack high-quality photos, we render only pickup locations images.
  const shouldRenderLocationImg = imageUrl !== undefined && !isOutpost;

  const shouldRenderMap = minWidth.isSM;
  const shouldRenderCTAs = currentBreakpoint.isXS;

  const hasSanitaryGrade =
    locationOrderChannel === 'pickup' &&
    typeof sanitaryGradeUrl === 'string' &&
    typeof sanitaryGradeDisplayText === 'string' &&
    sanitaryGradeUrl.length > 0 &&
    sanitaryGradeDisplayText.length > 0;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOrderNowBtnPress = useCallback(() => {
    onRequestClose();
    onOrderNowBtnPress(location, { entryPoint: 'details' });
  }, [location, onOrderNowBtnPress, onRequestClose]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const innerContainerStyles = [
    styles.modalInnerContainer,
    match([styles.modalInnerContainerXS, styles.modalInnerContainerSM]),
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={visible}
      accessibilityLabel={formatMessage(messages.locationDetailsTitle, {
        name,
      })}
      size={match(['full', 'large'])}
      offset={match([undefined, '10%'])}
      onRequestClose={onRequestClose}
      maxWidth={1024}
      fitHeight={currentBreakpoint.isXS}
      {...getIosModalPresentationConfig()}
    >
      <SafeAreaView edges={SAFE_AREA_EDGES} style={innerContainerStyles}>
        {shouldRenderMap ? (
          <View style={[styles.mapContainer, match([undefined, styles.mapContainerSM])]}>
            <LocationMap location={location} />
          </View>
        ) : null}

        <View style={match([styles.modalContentContainerXS, styles.modalContentContainerSM])}>
          <Modal.CloseBtn onPress={onRequestClose} />

          <ScrollView>
            {/*  ─── Location Image ────────────────────────────────────────────────── */}

            {shouldRenderLocationImg ? (
              <Modal.Row>
                <View style={[styles.imgContainer, styles.imgContainerWithBgColor]}>
                  <CloudinaryImage
                    baseUrl={imageUrl}
                    contentFit="cover"
                    style={styles.img}
                    aria-label={name}
                    config={IMG_CLOUDINARY_CONFIG}
                  />
                </View>
              </Modal.Row>
            ) : null}

            {/*  ─── Location Info ─────────────────────────────────────────────────── */}

            <Modal.Row style={styles.locationInfoRow}>
              <BodyText
                bold
                size={match([1, 2])}
                style={match([styles.sectionTextXS, styles.sectionTextSM])}
              >
                {name}
              </BodyText>

              {isAcceptingOrders ? null : (
                <LocationClosedNotice notAcceptingOrdersReason={notAcceptingOrdersReason} />
              )}

              <LocationInfo location={location} addressDetails={addressDetails} />

              {flexMessage ? <LocationFlexMessage flexMessage={flexMessage} /> : null}

              {hasSanitaryGrade && currentBreakpoint.isXS ? (
                <View style={styles.sanitaryGradeTextContainerXS}>
                  <TextLink href={sanitaryGradeUrl} sizeMatch={['16']}>
                    {sanitaryGradeDisplayText}
                  </TextLink>
                </View>
              ) : null}
            </Modal.Row>

            {shouldRenderCTAs ? (
              <Modal.Row style={styles.ctaContainer}>
                <LocationResultCardCTAs location={location} />
              </Modal.Row>
            ) : null}

            {hasSanitaryGrade && minWidth.isSM ? (
              <Modal.Row style={styles.sanitaryGradeTextContainerSM}>
                <TextLink href={sanitaryGradeUrl} sizeMatch={['16']}>
                  {sanitaryGradeDisplayText}
                </TextLink>
              </Modal.Row>
            ) : null}
          </ScrollView>

          {/*  ───────────────────────────────────────────────────────────────────── */}

          <Modal.Footer withoutTopBorder={minWidth.isSM} style={styles.footer}>
            <Button
              onPress={handleOrderNowBtnPress}
              size="large"
              isLoading={isLoading}
              disabled={isDisabled || !isAcceptingOrders}
            >
              {ctaLabel}
            </Button>
          </Modal.Footer>
        </View>
      </SafeAreaView>
    </Modal>
  );
});

// ─── Subcomponents ───────────────────────────────────────────────────────────

const LocationMap = memo((props: Pick<LocationResultCardModalProps, 'location'>) => {
  const { location } = props;

  const {
    name,
    acceptingOrders,
    address,
    id,
    isOutpost,
    lat = 0,
    lng = 0,
    restaurantSlug,
  } = location;

  // ─── Derived Data ────────────────────────────────────────────────────

  const locationPin = useMemo<readonly PinData[]>(() => {
    return [
      {
        id,
        name,
        lat,
        lng,
        address,
        restaurantSlug,
        restaurantName: name,
        acceptingOrders,
        isOutpost,
      },
    ];
  }, [acceptingOrders, address, id, isOutpost, lat, lng, name, restaurantSlug]);

  const mapRegion = useMemo<Region>(() => {
    return { ...findCenterRegion(locationPin), zoom: 14 };
  }, [locationPin]);

  return <Map publishableKey={RADAR_SDK_KEY} region={mapRegion} pins={locationPin} />;
});

const LocationClosedNotice = memo((props: Pick<Location, 'notAcceptingOrdersReason'>) => {
  const { notAcceptingOrdersReason } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  return (
    <>
      <BodyText
        size={match([2, 3])}
        style={[styles.closedLabel, match([styles.sectionTextXS, styles.sectionTextSM])]}
      >
        {formatMessage(messages.locationClosed)}
      </BodyText>

      {notAcceptingOrdersReason ? (
        <View style={match([styles.sectionTextXS, styles.sectionTextSM])}>
          <TagAlert size="small" palette="caution">
            {notAcceptingOrdersReason}
          </TagAlert>
        </View>
      ) : null}
    </>
  );
});

const LocationFlexMessage = memo((props: Pick<Location, 'flexMessage'>) => {
  const { flexMessage } = props;

  const { match } = useResponsive();

  return (
    <BodyText size={match([2, 3])} style={match([styles.sectionTextXS, styles.sectionTextSM])}>
      {flexMessage}
    </BodyText>
  );
});

const LocationInfo = memo(
  (props: Pick<LocationResultCardModalProps, 'addressDetails' | 'location'>) => {
    const { addressDetails, location } = props;
    const { address, storeHours, estimatedDeliveryTime } = location;

    const { match } = useResponsive();

    return (
      <View>
        <BodyText>{address}</BodyText>

        {addressDetails ? (
          <BodyText
            size={match([2, 3])}
            style={match([styles.sectionTextXS, styles.sectionTextSM])}
          >
            {addressDetails}
          </BodyText>
        ) : null}

        {storeHours ? (
          <BodyText
            size={match([2, 3])}
            style={match([styles.sectionTextXS, styles.sectionTextSM])}
          >
            {storeHours}
          </BodyText>
        ) : null}

        {estimatedDeliveryTime ? (
          <BodyText
            size={match([2, 3])}
            style={match([styles.sectionTextXS, styles.sectionTextSM])}
          >
            {estimatedDeliveryTime}
          </BodyText>
        ) : null}
      </View>
    );
  },
);

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationOrderNow: {
    defaultMessage: 'Order now',
    description: 'Location | Order Now',
  },
  locationClosed: {
    defaultMessage: 'Closed',
    description: 'Location | Closed',
  },
  locationResultsCardOrderLater: {
    defaultMessage: 'Schedule for later',
    description: 'Location | Results | Card | Order Later',
  },
  locationResultsCardOrderNow: {
    defaultMessage: 'Order now',
    description: 'Location | Results | Card | Order Now',
  },
  locationDetailsTitle: {
    defaultMessage: 'Location details for {name}',
    description: 'Location | Details | Title',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const { RADAR_SDK_KEY } = getEnvVars();
const IMG_CLOUDINARY_CONFIG = { height: 200, crop: 'fill' } as const;
const SAFE_AREA_EDGES: Edges = Platform.select({
  android: ['top', 'bottom'],
  default: ['bottom'],
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  modalInnerContainer: {
    flexGrow: 1,
    flexDirection: 'row',
  },
  modalInnerContainerXS: {
    paddingBottom: Platform.select({
      android: theme.spacing['4'],
      default: 0,
    }),
  },
  modalInnerContainerSM: {
    height: 550,
    maxHeight: '100%',
  },
  modalContentContainerXS: {
    width: '100%',
  },
  modalContentContainerSM: {
    width: 375,
  },
  mapContainer: {
    width: '50%',
    borderRightWidth: 1,
    borderRightColor: theme.colors.DARK_KALE,
  },
  mapContainerSM: {
    flex: 1,
    width: 'auto',
  },
  imgContainer: {
    width: '100%',
    borderRadius: theme.radius.medium,
    marginBottom: theme.spacing['4'],
  },
  imgContainerWithBgColor: {
    backgroundColor: LOCATION_CARD_IMG_BG_COLOR,
  },
  img: {
    width: '100%',
    height: 200,
    borderRadius: theme.radius.medium,
  },
  closedLabel: {
    color: theme.colors.CAUTION,
  },
  locationInfoRow: {
    marginBottom: 'auto',
  },
  sectionTextXS: {
    marginBottom: theme.spacing['2'],
  },
  sectionTextSM: {
    marginBottom: 2,
  },
  ctaContainer: {
    paddingVertical: theme.spacing['2'],
  },
  noticeBannersStack: {
    ...StyleSheet.absoluteFillObject,
    top: 'auto',
  },
  sanitaryGradeTextContainerXS: {
    marginBottom: theme.spacing['4'],
  },
  sanitaryGradeTextContainerSM: {
    marginTop: theme.spacing['2'],
  },
  footer: {
    paddingTop: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultCardModalProps = Readonly<{
  location: Location;
  addressDetails?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  locationOrderChannel?: 'pickup' | 'delivery' | 'outpost';
  onOrderNowBtnPress: OnSelectedLocationCardPress;
}> &
  Pick<ComponentProps<typeof Modal>, 'visible' | 'onRequestClose'>;
